import dayjs from 'dayjs';

/**
 * Nghiệp vụ nhân sự – tuyển dụng (thuần logic, không phụ thuộc giao diện).
 * Ngày nghiệp vụ (ngày vào, hiệu lực HĐ, ngày nghỉ…) lưu dạng 'YYYY-MM-DD'; thời điểm thao tác lưu timestamp.
 * Các ngưỡng pháp lý (thời gian thử việc, báo trước, 36 tháng…) gom ở đầu file để dễ chỉnh khi pháp chế rà soát.
 */

/* ============================== Ngưỡng nghiệp vụ ============================== */

/** Báo trước khi HĐ xác định thời hạn hết hạn */
export const CONTRACT_WARN_DAYS = 30;
/** Hết hạn HĐ xác định thời hạn mà vẫn làm quá số ngày này thì coi như chuyển sang không xác định thời hạn */
export const AUTO_INDEFINITE_DAYS = 30;
/** HĐ xác định thời hạn dài tối đa (tháng) và số lần được ký */
export const FIXED_MAX_MONTHS = 36;
export const FIXED_MAX_TIMES = 2;
/** Nhắc đánh giá thử việc trước khi hết hạn */
export const PROBATION_WARN_DAYS = 7;
/** Lương thử việc tối thiểu so với lương chính thức */
export const PROBATION_MIN_RATE = 0.85;
/** Hạn đăng ký BHXH kể từ ngày HĐLĐ có hiệu lực */
export const INS_DEADLINE_DAYS = 30;
/** Hạn thanh toán các khoản khi nghỉ việc */
export const SETTLE_DAYS = 14;
/** Giấy phép / chứng chỉ sắp hết hạn */
export const LICENSE_WARN_DAYS = 90;
/** Khám sức khỏe định kỳ */
export const HEALTH_CHECK_DAYS = 365;
/** Nghỉ trong thời gian này kể từ ngày vào = nghỉ sớm (đo chất lượng tuyển) */
export const EARLY_LEAVE_DAYS = 90;

/* ============================== Danh mục ============================== */

export interface Branch { id: string; name: string; short: string; clinic: boolean }
export interface Dept { id: string; name: string; color: string }
export type Level = 'staff' | 'lead' | 'manager' | 'director';
export const LEVEL: Record<Level, string> = { staff: 'Nhân viên', lead: 'Trưởng nhóm', manager: 'Quản lý', director: 'Ban giám đốc' };
export type LicenseKind = 'gphn' | 'cert';
export const LICENSE_KIND: Record<LicenseKind, string> = { gphn: 'Giấy phép hành nghề', cert: 'Chứng chỉ chuyên môn' };
export interface Position {
  id: string; name: string; deptId: string; level: Level;
  /** Vị trí bắt buộc có giấy phép hành nghề / chứng chỉ */
  license?: LicenseKind;
  salaryMin: number; salaryMax: number;
}

export type Education = 'university' | 'college' | 'intermediate' | 'other';
export const EDUCATION: Record<Education, string> = {
  university: 'Đại học trở lên', college: 'Cao đẳng', intermediate: 'Trung cấp / chứng chỉ nghề', other: 'Phổ thông / khác',
};

export type Source = 'referral' | 'facebook' | 'topcv' | 'website' | 'school' | 'walkin';
export const SOURCE: Record<Source, string> = {
  referral: 'Nhân viên giới thiệu', facebook: 'Facebook / TikTok', topcv: 'TopCV / VietnamWorks', website: 'Website công ty', school: 'Trường đào tạo', walkin: 'Tự đến / khác',
};

/* ============================== Nhân sự ============================== */

export type DocKey = 'cccd' | 'cv' | 'health' | 'degree' | 'photo' | 'license';
export const DOC_KEYS: Record<DocKey, string> = {
  cccd: 'CCCD (bản sao)', cv: 'Sơ yếu lý lịch', health: 'Giấy khám sức khỏe', degree: 'Bằng cấp', photo: 'Ảnh 3x4', license: 'Giấy phép hành nghề / chứng chỉ',
};

export interface License { id: string; kind: LicenseKind; number: string; issuedAt: string; expiresAt?: string; scope?: string }
export interface Log { at: number; by: string; text: string }

export type LeaveType = 'voluntary' | 'dismissed' | 'discipline' | 'contract_end' | 'probation_fail';
export const LEAVE_TYPE: Record<LeaveType, string> = {
  voluntary: 'Tự nguyện nghỉ', dismissed: 'Công ty đơn phương chấm dứt HĐ', discipline: 'Sa thải (kỷ luật)', contract_end: 'Hết hạn HĐ, không gia hạn', probation_fail: 'Không đạt thử việc',
};
export const LEAVE_REASONS = [
  'Thu nhập chưa phù hợp', 'Môi trường / quản lý trực tiếp', 'Có cơ hội tốt hơn', 'Lý do cá nhân / gia đình', 'Chuyển nơi ở',
  'Áp lực doanh số', 'Không đáp ứng yêu cầu công việc', 'Vi phạm kỷ luật', 'Không đạt thử việc', 'Hết hạn HĐ không gia hạn',
];

export interface Employee {
  id: string; code: string; name: string; gender: 'Nam' | 'Nữ'; dob?: string; phone: string; email?: string; cccd?: string;
  branchId: string; positionId: string; education: Education; managerId?: string;
  /** Ngày vào làm */
  hireDate: string;
  /** Ngày làm việc cuối cùng (đặt khi lập thủ tục nghỉ việc) */
  leftDate?: string; leaveType?: LeaveType; leaveReason?: string;
  /** Không tái tuyển */
  noRehire?: boolean;
  /** Tạm hoãn HĐ (thai sản, nghỉ không lương…) */
  paused?: { from: string; to?: string; reason: string };
  source?: Source; requisitionId?: string; candidateId?: string;
  docs: Partial<Record<DocKey, boolean>>;
  licenses: License[];
  /** Ngày khám sức khỏe gần nhất */
  healthCheck?: string;
  /** Người phụ trách chuyên môn của cơ sở */
  medicalLead?: boolean;
  /** Ngày báo tăng / báo giảm BHXH */
  ins: { increasedAt?: string; decreasedAt?: string };
  note?: string;
  createdAt: number;
  log: Log[];

  /* ----- Hồ sơ mở rộng (trang chi tiết) ----- */
  marital?: Marital;
  /** Lịch làm việc: ca hành chính / xoay ca */
  schedule?: string;
  taxCode?: string; dependents?: number;
  insuranceNo?: string; insurancePlace?: string;
  cccdDate?: string; cccdPlace?: string;
  address?: string; permanentAddress?: string; birthPlace?: string; nationality?: string;
  otherPhone?: string; otherEmail?: string; nickname?: string; facebook?: string;
  emergency?: { name: string; relation: string; phone: string };
  bank?: { account: string; bank: string; branch?: string };
  schools?: School[];
  workHistory?: WorkItem[];
  assets?: AssetItem[];
  records?: RecordItem[];
  moves?: MoveItem[];
  dependentList?: Dependent[];
  /** Mã chấm công (mặc định = mã nhân sự) */
  timeCode?: string;
  /** Số ngày phép năm đã nghỉ trong năm và phép năm trước chuyển sang */
  leaveUsed?: number; leaveCarried?: number;
  leaveRecords?: LeaveRecord[];
}

export type Marital = 'single' | 'married' | 'divorced';
export const MARITAL: Record<Marital, string> = { single: 'Độc thân', married: 'Đã kết hôn', divorced: 'Ly hôn' };
export const SCHEDULES = ['Hành chính (8:00 – 17:00)', 'Xoay ca (sáng / chiều / tối)', 'Ca gãy cuối tuần', 'Bán thời gian'];

export interface School { id: string; school: string; place?: string; from?: string; to?: string; degree: string; major?: string; highest?: boolean }
export interface WorkItem { id: string; company: string; position: string; from?: string; to?: string; note?: string }

export type AssetKind = 'device' | 'uniform' | 'card' | 'tool' | 'other';
export const ASSET_KIND: Record<AssetKind, string> = { device: 'Thiết bị', uniform: 'Đồng phục', card: 'Thẻ / chìa khóa', tool: 'Dụng cụ chuyên môn', other: 'Khác' };
export interface AssetItem { id: string; kind: AssetKind; name: string; code: string; issuedAt: string; condition?: string; returnedAt?: string; note?: string; value?: number }

export type RecordKind = 'work' | 'asset' | 'reward';
export const RECORD_KIND: Record<RecordKind, string> = { work: 'Vi phạm quy định làm việc', asset: 'Vi phạm chính sách tài sản', reward: 'Khen thưởng' };
export const DISCIPLINE_LEVELS = ['Nhắc nhở', 'Khiển trách', 'Kéo dài thời hạn nâng lương', 'Cách chức', 'Sa thải'];
export interface RecordItem { id: string; kind: RecordKind; date: string; title: string; level?: string; amount?: number; note?: string; by: string }

/** Tài sản chưa thu hồi */
export const unreturnedAssets = (e: Employee) => (e.assets || []).filter(a => !a.returnedAt);

/* ----- Quyết định điều chuyển / bổ nhiệm / điều chỉnh lương ----- */
export type MoveType = 'promotion' | 'transfer' | 'rotation' | 'salary';
export const MOVE_TYPE: Record<MoveType, { label: string; tag: string }> = {
  promotion: { label: 'Thăng chức / bổ nhiệm', tag: 'BỔ NHIỆM' },
  transfer: { label: 'Điều chuyển chi nhánh / bộ phận', tag: 'ĐIỀU CHUYỂN' },
  rotation: { label: 'Luân chuyển có thời hạn', tag: 'LUÂN CHUYỂN' },
  salary: { label: 'Điều chỉnh lương', tag: 'ĐIỀU CHỈNH LƯƠNG' },
};
export interface MoveItem {
  id: string; decisionNo: string; type: MoveType; date: string; until?: string;
  fromPositionId: string; toPositionId: string; fromBranchId: string; toBranchId: string;
  prevSalary?: number; salary?: number; reason?: string; by: string;
  /** Đã áp vào hồ sơ (quyết định tương lai sẽ áp khi tới ngày) */
  applied: boolean;
}

/* ----- Người phụ thuộc (giảm trừ gia cảnh) ----- */
export const DEP_RELATIONS = ['Con', 'Vợ / chồng', 'Bố / mẹ', 'Bố / mẹ vợ (chồng)', 'Khác'];
export interface Dependent { id: string; name: string; relation: string; dob?: string; taxCode?: string; from: string; to?: string }
/** Người phụ thuộc đang được giảm trừ tại ngày d */
export const activeDependents = (e: Employee, d: string) => (e.dependentList || []).filter(x => x.from <= d.slice(0, 7) && (!x.to || x.to >= d.slice(0, 7)));

/* ============================== Hợp đồng ============================== */

export type ContractKind = 'probation' | 'fixed' | 'indefinite' | 'service';
export const CONTRACT_KIND: Record<ContractKind, { label: string; short: string; badge: string }> = {
  probation: { label: 'Thỏa thuận thử việc', short: 'Thử việc', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  fixed: { label: 'HĐLĐ xác định thời hạn', short: 'Xác định TH', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  indefinite: { label: 'HĐLĐ không xác định thời hạn', short: 'Không XĐTH', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  service: { label: 'HĐ dịch vụ (cộng tác viên)', short: 'CTV', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
};

export interface Evaluation { result: 'pass' | 'fail'; by: string; at: number; score?: number; note?: string }

export interface Contract {
  id: string; code: string; empId: string; kind: ContractKind;
  start: string; end?: string;
  salary: number;
  /** Thử việc: lương chính thức đã thỏa thuận (để kiểm tra mức tối thiểu 85%) */
  officialSalary?: number;
  /** Ngày ký; chưa có = chưa ký */
  signedAt?: string;
  cancelled?: boolean;
  evaluation?: Evaluation;
  note?: string;
  createdBy: string; createdAt: number;
}

/* ============================== Nghỉ việc ============================== */

export type OffTask = 'handover' | 'assets' | 'accounts' | 'timesheet' | 'insurance' | 'settlement';
export const OFF_TASKS: Record<OffTask, string> = {
  handover: 'Bàn giao công việc, khách đang điều trị',
  assets: 'Thu hồi tài sản, đồng phục, thẻ ra vào',
  accounts: 'Khóa tài khoản phần mềm',
  timesheet: 'Chốt công, phép năm còn lại',
  insurance: 'Báo giảm BHXH, chốt sổ',
  settlement: 'Thanh toán lương và các khoản',
};

export interface Offboarding {
  id: string; empId: string; type: LeaveType;
  noticeDate: string; lastDay: string; reason: string; note?: string;
  tasks: Partial<Record<OffTask, { at: number; by: string }>>;
  status: 'open' | 'done' | 'cancelled';
  noRehire?: boolean;
  createdBy: string; createdAt: number; doneAt?: number;
  log: Log[];
}

/* ============================== Tuyển dụng ============================== */

export type ReqReason = 'replace' | 'expand' | 'new_branch';
export const REQ_REASON: Record<ReqReason, string> = { replace: 'Thay thế người nghỉ', expand: 'Mở rộng / tăng định biên', new_branch: 'Mở chi nhánh mới' };
export type ReqStatus = 'pending' | 'approved' | 'rejected' | 'closed' | 'cancelled';
export const REQ_STATUS: Record<ReqStatus, { label: string; badge: string }> = {
  pending: { label: 'Chờ duyệt', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Đang tuyển', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  rejected: { label: 'Từ chối', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  closed: { label: 'Đã đóng', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Đã hủy', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
};

export interface Requisition {
  id: string; code: string;
  branchId: string; positionId: string; qty: number;
  reason: ReqReason;
  /** Nhân sự nghỉ cần thay thế */
  replaceEmpIds?: string[];
  salaryMin: number; salaryMax: number;
  deadline: string; note?: string;
  status: ReqStatus;
  /** Vượt định biên tại thời điểm tạo */
  overPlan?: boolean;
  createdBy: string; createdAt: number;
  approvedBy?: string; approvedAt?: number; rejectReason?: string;
  closedAt?: number; closeNote?: string;
  log: Log[];
}

export type Stage = 'applied' | 'screening' | 'interview' | 'skill' | 'offer' | 'hired';
export const STAGES: Stage[] = ['applied', 'screening', 'interview', 'skill', 'offer', 'hired'];
export const STAGE: Record<Stage, { label: string; short: string; color: string }> = {
  applied: { label: 'Nhận hồ sơ', short: 'Hồ sơ', color: '#94a3b8' },
  screening: { label: 'Sàng lọc CV', short: 'Sàng lọc', color: '#60a5fa' },
  interview: { label: 'Phỏng vấn HR', short: 'PV HR', color: '#818cf8' },
  skill: { label: 'PV chuyên môn / test tay nghề', short: 'Chuyên môn', color: '#a78bfa' },
  offer: { label: 'Offer', short: 'Offer', color: '#f59e0b' },
  hired: { label: 'Nhận việc', short: 'Nhận việc', color: '#10b981' },
};
export type Outcome = 'open' | 'rejected' | 'withdrawn' | 'hired';
export const OUTCOME: Record<Outcome, { label: string; badge: string }> = {
  open: { label: 'Đang xử lý', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  rejected: { label: 'Không đạt', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  withdrawn: { label: 'Ứng viên từ chối', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  hired: { label: 'Đã nhận việc', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
export const REJECT_REASONS = ['CV không phù hợp', 'Thiếu kinh nghiệm', 'Thiếu giấy phép / chứng chỉ', 'Không đạt phỏng vấn', 'Không đạt test tay nghề', 'Kỳ vọng lương quá cao', 'Thái độ / giao tiếp chưa phù hợp'];
export const WITHDRAW_REASONS = ['Từ chối offer – lương', 'Nhận việc nơi khác', 'Không liên lạc được', 'Không đến phỏng vấn', 'Lý do cá nhân'];

export interface StageStep { stage: Stage; at: number; by: string; note?: string; score?: number }
export interface Offer {
  salary: number; startDate: string;
  status: 'need_approval' | 'sent' | 'accepted' | 'declined';
  approvedBy?: string; at: number; resolvedAt?: number; declineReason?: string;
}
export interface Candidate {
  id: string; name: string; phone: string; email?: string; cccd?: string; gender: 'Nam' | 'Nữ'; dob?: string;
  education: Education; experienceYears?: number; expectedSalary?: number;
  source: Source; requisitionId: string;
  stage: Stage; outcome: Outcome;
  history: StageStep[];
  closeReason?: string; closedAt?: number;
  offer?: Offer;
  empId?: string;
  appliedAt: number;
  note?: string;
  createdBy: string;
  /** Lịch phỏng vấn (mọi lần hẹn, kể cả đã đổi / vắng mặt) */
  interviews?: Interview[];
  /** Giấy phép hành nghề / chứng chỉ khai khi ứng tuyển — vị trí bắt buộc phải xác minh bản gốc trước khi offer */
  license?: CandidateLicense;
  /** Đồng ý xử lý dữ liệu cá nhân (Nghị định 13/2023/NĐ-CP) */
  consent?: Consent;
  /** Đã xóa dữ liệu cá nhân (hết hạn lưu trữ hoặc ứng viên yêu cầu) — chỉ giữ số liệu thống kê */
  anonymizedAt?: number;
  /** Đã nhận offer nhưng không đến nhận việc: hồ sơ nhân sự tạo trước đã được hủy */
  noShow?: { at: number; by: string; empCode: string; reason: string };
  /** Kho ứng viên: HR chủ động lưu (pool) hoặc bỏ khỏi danh sách gợi ý (poolHidden) */
  pool?: { at: number; by: string };
  poolHidden?: boolean;
  /** Hồ sơ được mời lại từ kho ứng viên */
  fromCandidateId?: string;
}

/* ----- Lịch phỏng vấn ----- */
export type InterviewStage = 'interview' | 'skill';
export type InterviewStatus = 'scheduled' | 'done' | 'noshow' | 'cancelled';
export const INTERVIEW_STATUS: Record<InterviewStatus, { label: string; badge: string }> = {
  scheduled: { label: 'Đã hẹn', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  done: { label: 'Đã phỏng vấn', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  noshow: { label: 'Không đến', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  cancelled: { label: 'Đã hủy / đổi lịch', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
};
export interface Interview {
  id: string; stage: InterviewStage;
  /** Thời điểm hẹn (timestamp) */
  at: number;
  place: string; interviewer: string; note?: string;
  status: InterviewStatus; by: string; createdAt: number;
  resolvedAt?: number; resolvedNote?: string;
}
export const INTERVIEW_ONLINE = ['Online (Google Meet / Zalo video)', 'Gọi điện thoại'];
/** Một buổi phỏng vấn chiếm bao nhiêu phút (để cảnh báo trùng lịch người phỏng vấn) */
export const INTERVIEW_SLOT_MIN = 45;
export const isInterviewStage = (st: Stage): st is InterviewStage => st === 'interview' || st === 'skill';
/** Lịch hẹn đang chờ cho bước hiện tại của ứng viên */
export const pendingInterview = (c: Candidate) =>
  c.outcome === 'open' && isInterviewStage(c.stage) ? (c.interviews || []).find(i => i.status === 'scheduled' && i.stage === c.stage) : undefined;
/** Ứng viên đang ở vòng phỏng vấn nhưng chưa có lịch hẹn */
export const needsSchedule = (c: Candidate) => c.outcome === 'open' && isInterviewStage(c.stage) && !pendingInterview(c);
/** Đã quá giờ hẹn mà chưa ghi kết quả */
export const interviewOverdue = (i: Interview, now = Date.now()) => i.status === 'scheduled' && i.at + INTERVIEW_SLOT_MIN * 60000 < now;

export function validateInterview(s: HrState, c: Candidate, d: { at?: number; place: string; interviewer: string }, now = Date.now()): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (c.outcome !== 'open' || !isInterviewStage(c.stage)) errors.push('Ứng viên không ở vòng phỏng vấn');
  if (!d.at) errors.push('Chọn ngày giờ phỏng vấn');
  else if (d.at < now - 5 * 60000) errors.push('Giờ hẹn đã qua');
  if (!d.place.trim()) errors.push('Nhập địa điểm hoặc hình thức phỏng vấn');
  if (!d.interviewer.trim()) errors.push('Chọn người phỏng vấn');
  if (d.at) {
    const h = dayjs(d.at).hour();
    if (h < 7 || h >= 20) warnings.push('Giờ hẹn ngoài khung 7:00 – 20:00');
    const name = normText(d.interviewer);
    s.candidates.forEach(x => (x.interviews || []).forEach(i => {
      if (x.id === c.id || i.status !== 'scheduled' || normText(i.interviewer) !== name) return;
      if (Math.abs(i.at - d.at!) < INTERVIEW_SLOT_MIN * 60000) warnings.push(`${d.interviewer.trim()} đã có lịch phỏng vấn ${x.name} lúc ${dayjs(i.at).format('HH:mm DD/MM')}`);
    }));
  }
  return { errors, warnings };
}

/* ----- Giấy phép hành nghề của ứng viên ----- */
export interface CandidateLicense {
  kind: LicenseKind; number: string; issuedAt: string; expiresAt?: string; scope?: string; issuer?: string;
  /** Người đã đối chiếu bản gốc (bắt buộc trước khi offer vị trí cần giấy phép) */
  verifiedBy?: string; verifiedAt?: number;
}
export function validateCandidateLicense(d: Partial<CandidateLicense>, today: string, startDate?: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!d.number?.trim()) errors.push('Nhập số giấy phép / chứng chỉ');
  if (!d.issuedAt) errors.push('Nhập ngày cấp');
  else if (d.issuedAt > today) errors.push('Ngày cấp sau ngày hôm nay');
  if (!d.issuer?.trim()) errors.push('Nhập nơi cấp');
  if (d.expiresAt) {
    if (d.issuedAt && d.expiresAt <= d.issuedAt) errors.push('Ngày hết hạn phải sau ngày cấp');
    else if (d.expiresAt <= today) errors.push(`Giấy phép đã hết hạn (${fmtD(d.expiresAt)})`);
    else if (startDate && d.expiresAt <= startDate) errors.push(`Giấy phép hết hạn (${fmtD(d.expiresAt)}) trước ngày nhận việc`);
    else if (daysBetween(today, d.expiresAt) <= LICENSE_WARN_DAYS) warnings.push(`Giấy phép hết hạn sau ${daysBetween(today, d.expiresAt)} ngày — yêu cầu gia hạn`);
  }
  return { errors, warnings };
}
/** Lý do chưa được offer vì giấy phép (vị trí bắt buộc giấy phép) */
export function licenseBlocker(s: HrState, c: Candidate, today: string): string | null {
  const need = positionById(s, reqById(s, c.requisitionId)?.positionId)?.license;
  if (!need) return null;
  if (!c.license) return `Vị trí bắt buộc ${LICENSE_KIND[need].toLowerCase()} — nhập và đối chiếu bản gốc trước khi offer`;
  if (!c.license.verifiedAt) return `Chưa đối chiếu bản gốc ${LICENSE_KIND[need].toLowerCase()} số ${c.license.number}`;
  if (c.license.expiresAt && c.license.expiresAt <= today) return `Giấy phép đã hết hạn (${fmtD(c.license.expiresAt)})`;
  return null;
}

/* ----- Dữ liệu cá nhân ứng viên (Nghị định 13/2023/NĐ-CP) ----- */
export interface Consent { at: number; by: string; channel: string }
export const CONSENT_CHANNELS = ['Ký phiếu ứng tuyển tại quầy', 'Tích đồng ý trên form ứng tuyển online', 'Xác nhận qua tin nhắn / email', 'Đồng ý qua điện thoại (có ghi âm)'];
export const CONSENT_TEXT = 'Ứng viên đồng ý để công ty thu thập và xử lý dữ liệu cá nhân (họ tên, liên hệ, CCCD, hồ sơ năng lực) cho mục đích tuyển dụng, lưu tối đa 12 tháng nếu không trúng tuyển.';
/** Hồ sơ không trúng tuyển giữ dữ liệu cá nhân tối đa (ngày), quá hạn thì xóa */
export const CANDIDATE_RETENTION_DAYS = 365;
export const ANON_NAME = 'Ứng viên đã xóa dữ liệu';
/** Hồ sơ đã đóng quá hạn lưu trữ, cần xóa dữ liệu cá nhân */
export const retentionDue = (s: HrState, today: string) => s.candidates.filter(c =>
  (c.outcome === 'rejected' || c.outcome === 'withdrawn') && !c.anonymizedAt && !!c.closedAt && daysBetween(toDay(c.closedAt), today) > CANDIDATE_RETENTION_DAYS);
/** Xóa dữ liệu nhận diện, giữ lại nguồn, bước, lý do… để thống kê */
export const anonymized = (c: Candidate, at: number): Candidate => ({
  ...c, name: ANON_NAME, phone: '', email: undefined, cccd: undefined, dob: undefined, note: undefined, license: undefined, anonymizedAt: at,
  history: c.history.map(h => ({ ...h, note: undefined })),
  interviews: c.interviews?.map(i => ({ ...i, note: undefined, resolvedNote: undefined })),
});

/* ----- Kho ứng viên ----- */
/** Điểm phỏng vấn cao nhất */
export const bestScore = (c: Candidate) => c.history.reduce((m, h) => Math.max(m, h.score || 0), 0) || undefined;
/** Lý do đóng hồ sơ không do năng lực ứng viên → nên giữ lại */
const POOL_CLOSE_REASONS = ['Vị trí đã đủ người', 'Vị trí đã đóng', 'Nhận việc nơi khác', 'Từ chối offer – lương', 'Lý do cá nhân'];
/** Gợi ý vào kho: lý do (null = không gợi ý) */
export function poolSuggestion(c: Candidate): string | null {
  const best = bestScore(c) || 0;
  if (c.outcome === 'withdrawn' && (c.stage === 'offer' || c.offer)) return 'Đã tới bước offer';
  // Lý do khách quan chỉ đáng giữ khi ứng viên đã qua sàng lọc, vào tới vòng phỏng vấn
  if (c.closeReason && POOL_CLOSE_REASONS.includes(c.closeReason) && STAGES.indexOf(c.stage) >= STAGES.indexOf('interview')) return c.closeReason;
  if (best >= 7) return `Điểm phỏng vấn ${best}/10`;
  return null;
}
export interface PoolRow { c: Candidate; why: string; best?: number; expiresIn: number; reinvited?: Candidate }
/** Kho ứng viên: hồ sơ đã đóng còn dữ liệu, có đồng ý xử lý dữ liệu, không vướng không tái tuyển / đang làm việc */
export function talentPool(s: HrState, today: string): PoolRow[] {
  return s.candidates.flatMap(c => {
    if ((c.outcome !== 'rejected' && c.outcome !== 'withdrawn') || c.anonymizedAt || c.noShow || !c.closedAt || !c.consent) return [];
    const why = c.pool ? 'HR lưu vào kho' : c.poolHidden ? null : poolSuggestion(c);
    if (!why) return [];
    const expiresIn = CANDIDATE_RETENTION_DAYS - daysBetween(toDay(c.closedAt), today);
    if (expiresIn < 0) return [];
    const reinvited = s.candidates.find(x => x.fromCandidateId === c.id && x.outcome !== 'rejected' && x.outcome !== 'withdrawn');
    if (!reinvited) {
      const dup = candidateDupCheck(s, { phone: c.phone, cccd: c.cccd, id: c.id }, today);
      if (dup.errors.length) return [];
    }
    return [{ c, why, best: bestScore(c), expiresIn, reinvited }];
  }).sort((a, b) => (b.c.closedAt || 0) - (a.c.closedAt || 0));
}

/* ----- Không đến nhận việc ----- */
/** Sau ngày nhận việc bao nhiêu ngày vẫn được ghi nhận "không đến nhận việc" (hủy hồ sơ tạo trước) */
export const NO_SHOW_GRACE_DAYS = 7;
export const NO_SHOW_REASONS = ['Không đến, không liên lạc được', 'Báo nhận việc nơi khác', 'Lý do cá nhân / gia đình', 'Không hoàn thiện được hồ sơ'];
/** Có được hủy hồ sơ nhân sự vừa tạo từ tuyển dụng vì người đó không đến nhận việc không */
export function noShowCheck(s: HrState, e: Employee, today: string): { ok: boolean; reason?: string } {
  const c = candById(s, e.candidateId);
  if (!c || c.outcome !== 'hired' || c.empId !== e.id) return { ok: false, reason: 'Hồ sơ không tạo từ tuyển dụng' };
  if (e.leftDate) return { ok: false, reason: 'Nhân sự đã có thủ tục nghỉ việc' };
  if (daysBetween(e.hireDate, today) > NO_SHOW_GRACE_DAYS) return { ok: false, reason: `Đã quá ${NO_SHOW_GRACE_DAYS} ngày kể từ ngày nhận việc — lập thủ tục nghỉ việc` };
  if (unreturnedAssets(e).length) return { ok: false, reason: 'Còn tài sản đã cấp phát chưa thu hồi' };
  if ((e.moves || []).length || (e.leaveRecords || []).length) return { ok: false, reason: 'Hồ sơ đã phát sinh quyết định / nghỉ phép' };
  if (s.contracts.some(x => x.empId === e.id && x.kind !== 'probation')) return { ok: false, reason: 'Đã có hợp đồng lao động' };
  return { ok: true };
}

/* ============================== State ============================== */

export interface HrState {
  v: 1; seededAt: number; demoVersion?: number;
  branches: Branch[]; depts: Dept[]; positions: Position[];
  employees: Employee[]; contracts: Contract[]; offboardings: Offboarding[];
  requisitions: Requisition[]; candidates: Candidate[];
  /** Định biên: key `${branchId}|${deptId}` → số người */
  plan: Record<string, number>;
}

/* ============================== Tiện ích ngày & định dạng ============================== */

export const toDay = (t: number | string = Date.now()) => dayjs(t).format('YYYY-MM-DD');
/** Số ngày từ a đến b (b − a) */
export const daysBetween = (a: string, b: string) => dayjs(b).startOf('day').diff(dayjs(a).startOf('day'), 'day');
export const addDays = (d: string, n: number) => dayjs(d).add(n, 'day').format('YYYY-MM-DD');
export const addMonths = (d: string, n: number) => dayjs(d).add(n, 'month').format('YYYY-MM-DD');
/** Cộng n ngày làm việc (bỏ Chủ nhật), tính cả ngày bắt đầu */
export function addWorkingDays(start: string, n: number) {
  let d = dayjs(start);
  let left = n;
  for (;;) {
    if (d.day() !== 0) left -= 1;
    if (left <= 0) return d.format('YYYY-MM-DD');
    d = d.add(1, 'day');
  }
}
export const fmtD = (d?: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');
export const fmtNum = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n));
export const fmtMoney = (n: number) => `${fmtNum(n)} ₫`;
/** 12.500.000 → "12,5 tr" */
export const fmtM = (n: number) => (Math.abs(n) >= 1e6 ? `${fmtNum(Math.round(n / 1e5) / 10)} tr` : fmtNum(n));
/** "2 năm 3 tháng" */
export function tenureText(from: string, to: string) {
  const m = Math.max(0, dayjs(to).diff(dayjs(from), 'month'));
  if (m < 1) return `${Math.max(0, daysBetween(from, to))} ngày`;
  const y = Math.floor(m / 12);
  return [y ? `${y} năm` : '', m % 12 ? `${m % 12} tháng` : ''].filter(Boolean).join(' ');
}
export const pct = (a: number, b: number) => (b ? Math.round((a / b) * 1000) / 10 : 0);

/* ============================== Tra cứu ============================== */

export const branchById = (s: HrState, id?: string) => s.branches.find(b => b.id === id);
export const deptById = (s: HrState, id?: string) => s.depts.find(d => d.id === id);
export const positionById = (s: HrState, id?: string) => s.positions.find(p => p.id === id);
export const empById = (s: HrState, id?: string) => s.employees.find(e => e.id === id);
export const reqById = (s: HrState, id?: string) => s.requisitions.find(r => r.id === id);
export const candById = (s: HrState, id?: string) => s.candidates.find(c => c.id === id);
export const deptOf = (s: HrState, e: Pick<Employee, 'positionId'>) => positionById(s, e.positionId)?.deptId || '';
export const planKey = (branchId: string, deptId: string) => `${branchId}|${deptId}`;

/* ============================== Trạng thái nhân sự ============================== */

/** Đang làm việc tại ngày d (tính cả người đang tạm hoãn HĐ) */
export const isWorking = (e: Employee, d: string) => e.hireDate <= d && (!e.leftDate || d <= e.leftDate);
export const isPaused = (e: Employee, d: string) => !!e.paused && e.paused.from <= d && (!e.paused.to || d <= e.paused.to);

export const empContracts = (s: HrState, empId: string) =>
  s.contracts.filter(c => c.empId === empId && !c.cancelled).sort((a, b) => a.start.localeCompare(b.start) || a.createdAt - b.createdAt);

/** HĐ đang áp dụng tại ngày d (HĐ có hiệu lực gần nhất) */
export function currentContract(s: HrState, empId: string, d: string) {
  const list = empContracts(s, empId).filter(c => c.start <= d);
  return list[list.length - 1];
}
export const nextContract = (s: HrState, c: Contract) => empContracts(s, c.empId).find(x => x.start > c.start);

export type Phase = 'upcoming' | 'probation' | 'fixed' | 'indefinite' | 'ctv' | 'nocontract' | 'paused' | 'left';
export const PHASE: Record<Phase, { label: string; badge: string }> = {
  upcoming: { label: 'Sắp nhận việc', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  probation: { label: 'Thử việc', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  fixed: { label: 'Chính thức (XĐTH)', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  indefinite: { label: 'Chính thức (không XĐTH)', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ctv: { label: 'Cộng tác viên', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  nocontract: { label: 'Chưa có HĐ', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  paused: { label: 'Tạm hoãn HĐ', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
  left: { label: 'Đã nghỉ', badge: 'bg-gray-100 text-gray-500 border-gray-200' },
};

export function phase(s: HrState, e: Employee, today: string): Phase {
  if (e.hireDate > today) return 'upcoming';
  if (!isWorking(e, today)) return 'left';
  if (isPaused(e, today)) return 'paused';
  const c = currentContract(s, e.id, today);
  if (!c) return 'nocontract';
  return c.kind === 'service' ? 'ctv' : c.kind;
}

/** Nhân sự tính vào quân số (lao động có HĐLĐ / thử việc, không tính CTV) đang làm tại ngày d */
export function isStaffAt(s: HrState, e: Employee, d: string) {
  if (!isWorking(e, d)) return false;
  const c = currentContract(s, e.id, d);
  return !c || c.kind !== 'service';
}

export interface Scope { branchId?: string; deptId?: string }
export const inScope = (s: HrState, e: Employee, sc: Scope = {}) =>
  (!sc.branchId || e.branchId === sc.branchId) && (!sc.deptId || deptOf(s, e) === sc.deptId);

export const headcountAt = (s: HrState, d: string, sc: Scope = {}) =>
  s.employees.filter(e => inScope(s, e, sc) && isStaffAt(s, e, d)).length;

/* ============================== Hợp đồng: trạng thái & quy tắc ============================== */

export type CState = 'upcoming' | 'active' | 'expiring' | 'expired' | 'auto_indefinite' | 'eval_due' | 'eval_overdue' | 'renewed' | 'leaving' | 'ended';
export const CSTATE: Record<CState, { label: string; badge: string; level?: 'crit' | 'warn' }> = {
  upcoming: { label: 'Chưa hiệu lực', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  active: { label: 'Đang hiệu lực', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expiring: { label: 'Sắp hết hạn', badge: 'bg-amber-50 text-amber-700 border-amber-200', level: 'warn' },
  expired: { label: 'Quá hạn, chưa gia hạn', badge: 'bg-rose-50 text-rose-700 border-rose-200', level: 'crit' },
  auto_indefinite: { label: 'Quá hạn > 30 ngày', badge: 'bg-rose-100 text-rose-800 border-rose-300', level: 'crit' },
  eval_due: { label: 'Sắp hết thử việc', badge: 'bg-amber-50 text-amber-700 border-amber-200', level: 'warn' },
  eval_overdue: { label: 'Quá hạn đánh giá', badge: 'bg-rose-50 text-rose-700 border-rose-200', level: 'crit' },
  renewed: { label: 'Đã có HĐ tiếp theo', badge: 'bg-teal-50 text-teal-700 border-teal-200' },
  leaving: { label: 'Đang làm thủ tục nghỉ', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  ended: { label: 'Đã kết thúc', badge: 'bg-gray-100 text-gray-500 border-gray-200' },
};

export function contractState(s: HrState, c: Contract, today: string): CState {
  const e = empById(s, c.empId);
  const next = nextContract(s, c);
  if (next && next.start <= today) return 'ended';
  if (!e || (e.leftDate && e.leftDate < today)) return 'ended';
  if (c.kind === 'probation' && c.evaluation?.result === 'fail') return e.leftDate ? 'leaving' : 'ended';
  if (c.start > today) return 'upcoming';
  if (next) return 'renewed';
  if (e.leftDate) return 'leaving';
  if (!c.end) return 'active';
  const left = daysBetween(today, c.end);
  if (c.kind === 'probation') {
    // Đã đạt nhưng chưa có HĐ chính thức: quá hạn thử việc là làm việc không có HĐ
    if (c.evaluation?.result === 'pass') return left < 0 ? 'expired' : 'active';
    if (left < 0) return 'eval_overdue';
    return left <= PROBATION_WARN_DAYS ? 'eval_due' : 'active';
  }
  if (left < -AUTO_INDEFINITE_DAYS && c.kind === 'fixed') return 'auto_indefinite';
  if (left < 0) return 'expired';
  return left <= CONTRACT_WARN_DAYS ? 'expiring' : 'active';
}

/** Số HĐ xác định thời hạn đã ký (quy tắc: tối đa 2 lần, lần sau phải không xác định thời hạn) */
export const fixedCount = (s: HrState, empId: string, exceptId?: string) =>
  empContracts(s, empId).filter(c => c.kind === 'fixed' && c.id !== exceptId).length;

/** Thời gian thử việc tối đa theo cấp bậc / trình độ */
export function probationLimit(level: Level, edu: Education): { days: number; working: boolean; label: string } {
  if (level === 'director') return { days: 180, working: false, label: 'Quản lý doanh nghiệp: tối đa 180 ngày' };
  if (edu === 'university' || edu === 'college') return { days: 60, working: false, label: 'Trình độ cao đẳng trở lên: tối đa 60 ngày' };
  if (edu === 'intermediate') return { days: 30, working: false, label: 'Trung cấp / chứng chỉ nghề: tối đa 30 ngày' };
  return { days: 6, working: true, label: 'Công việc khác: tối đa 6 ngày làm việc' };
}
export const probationEndOf = (start: string, lim: { days: number; working: boolean }) =>
  lim.working ? addWorkingDays(start, lim.days) : addDays(start, lim.days - 1);
export const empProbationLimit = (s: HrState, e: Employee) => probationLimit(positionById(s, e.positionId)?.level || 'staff', e.education);

export type ContractDraft = Omit<Contract, 'id' | 'code' | 'createdBy' | 'createdAt'> & { id?: string };

/** Kiểm tra HĐ trước khi lưu: lỗi chặn lưu, cảnh báo chỉ nhắc */
export function validateContract(s: HrState, d: ContractDraft): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const e = empById(s, d.empId);
  if (!e) return { errors: ['Chọn nhân sự'], warnings };
  if (!d.start) errors.push('Nhập ngày hiệu lực');
  if (d.kind === 'indefinite' && d.end) errors.push('HĐ không xác định thời hạn không có ngày kết thúc');
  if (d.kind !== 'indefinite' && !d.end) errors.push('Nhập ngày kết thúc HĐ');
  if (d.start && d.end && d.end < d.start) errors.push('Ngày kết thúc phải sau ngày hiệu lực');
  if (!(d.salary > 0)) errors.push('Nhập mức lương');
  if (e.leftDate && d.start > e.leftDate) errors.push(`Nhân sự nghỉ việc từ ${fmtD(e.leftDate)}`);

  const others = empContracts(s, d.empId).filter(c => c.id !== d.id);
  const prev = others.filter(c => c.start <= (d.start || '9999')).pop();
  if (d.start && others.some(c => c.start === d.start)) errors.push('Đã có HĐ khác hiệu lực cùng ngày');
  if (d.start && d.start < e.hireDate) errors.push(`Ngày hiệu lực trước ngày vào làm (${fmtD(e.hireDate)})`);

  if (d.kind === 'fixed') {
    if (d.start && d.end && dayjs(d.end).diff(dayjs(d.start), 'month', true) > FIXED_MAX_MONTHS) errors.push(`HĐ xác định thời hạn tối đa ${FIXED_MAX_MONTHS} tháng`);
    const n = fixedCount(s, d.empId, d.id);
    if (n >= FIXED_MAX_TIMES) errors.push(`Đã ký ${n} HĐ xác định thời hạn — lần này phải ký HĐ không xác định thời hạn`);
  }
  if (d.kind === 'probation') {
    const lim = empProbationLimit(s, e);
    if (others.some(c => c.kind === 'probation')) errors.push('Mỗi nhân sự chỉ thử việc một lần cho một công việc');
    if (others.some(c => c.kind === 'fixed' || c.kind === 'indefinite')) errors.push('Nhân sự đã có HĐLĐ chính thức, không ký thử việc');
    if (d.start && d.end && d.end > probationEndOf(d.start, lim)) errors.push(`Thời gian thử việc vượt mức cho phép (${lim.label}, hết hạn muộn nhất ${fmtD(probationEndOf(d.start, lim))})`);
    if (!d.officialSalary) errors.push('Nhập lương chính thức đã thỏa thuận');
    else if (d.salary < d.officialSalary * PROBATION_MIN_RATE) errors.push(`Lương thử việc tối thiểu ${PROBATION_MIN_RATE * 100}% lương chính thức (${fmtMoney(Math.ceil(d.officialSalary * PROBATION_MIN_RATE))})`);
  }
  if (prev?.end && d.start && daysBetween(prev.end, d.start) > 1 && !e.leftDate) {
    warnings.push(`Có ${daysBetween(prev.end, d.start) - 1} ngày giữa HĐ trước (hết ${fmtD(prev.end)}) và HĐ này — nhân sự làm việc không có HĐ trong khoảng đó`);
  }
  const pos = positionById(s, e.positionId);
  const base = d.kind === 'probation' ? d.officialSalary || 0 : d.salary;
  if (pos && base && d.kind !== 'service' && (base < pos.salaryMin || base > pos.salaryMax)) {
    warnings.push(`Lương ${fmtMoney(base)} nằm ngoài khung vị trí ${pos.name} (${fmtM(pos.salaryMin)} – ${fmtM(pos.salaryMax)})`);
  }
  return { errors, warnings };
}

/** Gợi ý HĐ tiếp theo (gia hạn / ký chính thức) */
/** Lương đang áp dụng tại ngày d: HĐ hiện hành, hoặc quyết định điều chỉnh lương / bổ nhiệm mới hơn */
export function salaryAt(s: HrState, e: Employee, d: string) {
  const c = currentContract(s, e.id, d);
  const base = c?.salary || 0;
  const move = (e.moves || []).filter(m => m.salary && m.date <= d && (!c || m.date >= c.start)).sort((a, b) => a.date.localeCompare(b.date)).pop();
  return move?.salary || base;
}

export type MoveDraft = Omit<MoveItem, 'id' | 'decisionNo' | 'fromPositionId' | 'fromBranchId' | 'prevSalary' | 'applied' | 'by'> & { decisionNo?: string };

/** Kiểm tra quyết định điều chuyển / bổ nhiệm */
export function validateMove(s: HrState, e: Employee, d: MoveDraft, today: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const to = positionById(s, d.toPositionId);
  if (!d.date) errors.push('Nhập ngày hiệu lực');
  if (d.date && d.date < e.hireDate) errors.push('Ngày hiệu lực trước ngày vào làm');
  if (e.leftDate && d.date > e.leftDate) errors.push(`Nhân sự nghỉ việc từ ${fmtD(e.leftDate)}`);
  if (!to) errors.push('Chọn chức danh');
  if (!d.toBranchId) errors.push('Chọn chi nhánh');
  if (d.type === 'rotation' && (!d.until || d.until <= d.date)) errors.push('Luân chuyển có thời hạn: nhập ngày kết thúc sau ngày hiệu lực');
  const same = d.toPositionId === e.positionId && d.toBranchId === e.branchId;
  if (d.type !== 'salary' && same) errors.push('Chức danh và chi nhánh mới trùng hiện tại');
  if (d.type === 'salary' && !d.salary) errors.push('Nhập mức lương mới');
  const cur = salaryAt(s, e, d.date || today);
  if (d.salary && cur && d.salary < cur) warnings.push(`Lương mới thấp hơn hiện tại (${fmtMoney(cur)}) — giảm lương cần sự đồng ý bằng văn bản của người lao động`);
  if (to?.license) {
    const lic = licenseState(s, { ...e, positionId: to.id }, d.date || today);
    if (lic.state === 'missing' || lic.state === 'expired') {
      (to.license === 'gphn' ? errors : warnings).push(`Vị trí ${to.name} bắt buộc ${LICENSE_KIND[to.license].toLowerCase()} — nhân sự chưa có giấy phép còn hạn`);
    }
  }
  if (to && d.salary && (d.salary < to.salaryMin || d.salary > to.salaryMax)) warnings.push(`Lương ${fmtMoney(d.salary)} ngoài khung ${to.name} (${fmtM(to.salaryMin)} – ${fmtM(to.salaryMax)})`);
  if (to && d.toBranchId && (d.toBranchId !== e.branchId || to.deptId !== deptOf(s, e))) {
    const st = planStatus(s, d.toBranchId, to.deptId, today);
    if (st.actual - st.leaving + st.upcoming >= st.plan) warnings.push(`${deptById(s, to.deptId)?.name} · ${branchById(s, d.toBranchId)?.short} đã đủ định biên (${st.plan}) — điều chuyển sẽ vượt định biên`);
    const from = planStatus(s, e.branchId, deptOf(s, e), today);
    if (from.actual - from.leaving - 1 < from.plan) warnings.push(`${deptById(s, deptOf(s, e))?.name} · ${branchById(s, e.branchId)?.short} sẽ thiếu người so với định biên — cân nhắc tạo yêu cầu tuyển thay thế`);
  }
  if (e.medicalLead && (d.toBranchId !== e.branchId || to?.license !== 'gphn')) warnings.push(`Đang là người phụ trách chuyên môn ${branchById(s, e.branchId)?.short} — sau quyết định cần chỉ định người khác`);
  return { errors, warnings };
}

export function suggestNext(s: HrState, e: Employee, today: string): Omit<ContractDraft, 'empId'> {
  const cur = currentContract(s, e.id, today) || empContracts(s, e.id).pop();
  if (!cur) {
    const start = e.hireDate > today ? e.hireDate : today;
    return { kind: 'probation', start, end: probationEndOf(start, empProbationLimit(s, e)), salary: 0 };
  }
  const start = cur.end ? addDays(cur.end, 1) : today;
  const salary = cur.kind === 'probation' ? cur.officialSalary || cur.salary : salaryAt(s, e, today);
  if (cur.kind === 'service') return { kind: 'service', start, end: addDays(addMonths(start, 6), -1), salary };
  if (fixedCount(s, e.id) >= FIXED_MAX_TIMES) return { kind: 'indefinite', start, salary };
  return { kind: 'fixed', start, end: addDays(addMonths(start, 12), -1), salary };
}

/* ============================== Bảo hiểm, giấy phép, hồ sơ ============================== */

export type InsState = 'na' | 'ok' | 'missing' | 'late' | 'decrease_pending' | 'closed';
export const INS_STATE: Record<InsState, { label: string; badge: string }> = {
  na: { label: 'Không thuộc diện', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
  ok: { label: 'Đã báo tăng', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  missing: { label: 'Chưa báo tăng', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  late: { label: 'Trễ hạn báo tăng', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  decrease_pending: { label: 'Chưa báo giảm', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  closed: { label: 'Đã báo giảm', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
};

/** Ngày bắt đầu HĐLĐ chính thức đầu tiên (mốc tính hạn đăng ký BHXH) */
export const officialStart = (s: HrState, empId: string) =>
  empContracts(s, empId).find(c => (c.kind === 'fixed' || c.kind === 'indefinite') && c.signedAt)?.start;

export function insState(s: HrState, e: Employee, today: string): InsState {
  if (e.leftDate && e.leftDate < today) return e.ins.increasedAt && !e.ins.decreasedAt ? 'decrease_pending' : e.ins.increasedAt ? 'closed' : 'na';
  const cur = currentContract(s, e.id, today);
  if (!cur || (cur.kind !== 'fixed' && cur.kind !== 'indefinite') || !cur.signedAt) return e.ins.increasedAt ? 'ok' : 'na';
  if (e.ins.increasedAt) return 'ok';
  const from = officialStart(s, e.id) || cur.start;
  return daysBetween(from, today) > INS_DEADLINE_DAYS ? 'late' : 'missing';
}

export type LicState = 'na' | 'ok' | 'expiring' | 'expired' | 'missing';
export function licenseState(s: HrState, e: Employee, today: string): { need?: LicenseKind; lic?: License; state: LicState; days?: number } {
  const need = positionById(s, e.positionId)?.license;
  if (!need) return { state: 'na' };
  const lic = e.licenses.filter(l => l.kind === need).sort((a, b) => (b.expiresAt || '9999').localeCompare(a.expiresAt || '9999'))[0];
  if (!lic) return { need, state: 'missing' };
  if (!lic.expiresAt) return { need, lic, state: 'ok' };
  const days = daysBetween(today, lic.expiresAt);
  return { need, lic, days, state: days < 0 ? 'expired' : days <= LICENSE_WARN_DAYS ? 'expiring' : 'ok' };
}

export function healthState(e: Employee, today: string): { state: 'ok' | 'due' | 'overdue' | 'missing'; days?: number } {
  if (!e.healthCheck) return { state: 'missing' };
  const days = HEALTH_CHECK_DAYS - daysBetween(e.healthCheck, today);
  return { state: days < 0 ? 'overdue' : days <= 30 ? 'due' : 'ok', days };
}

export function requiredDocs(s: HrState, e: Employee): DocKey[] {
  const keys: DocKey[] = ['cccd', 'cv', 'health', 'photo'];
  const pos = positionById(s, e.positionId);
  if (e.education !== 'other' || pos?.license) keys.push('degree');
  if (pos?.license) keys.push('license');
  return keys;
}
export const missingDocs = (s: HrState, e: Employee) => requiredDocs(s, e).filter(k => !e.docs[k]);

/** Người phụ trách chuyên môn hợp lệ (có giấy phép còn hạn) của từng cơ sở */
export function medicalLeadOf(s: HrState, branchId: string, today: string) {
  return s.employees.find(e => e.branchId === branchId && e.medicalLead && isWorking(e, today) && ['ok', 'expiring'].includes(licenseState(s, e, today).state));
}

/* ============================== Nghỉ việc ============================== */

/** Thời gian báo trước tối thiểu theo loại HĐ đang áp dụng */
export function requiredNotice(s: HrState, empId: string, type: LeaveType, at: string): { days: number; working: boolean; label: string } {
  const c = currentContract(s, empId, at);
  if (type === 'contract_end' || type === 'probation_fail') return { days: 0, working: false, label: 'Thông báo bằng văn bản cho người lao động' };
  if (type === 'discipline') return { days: 0, working: false, label: 'Sa thải theo quy trình xử lý kỷ luật: không áp dụng thời hạn báo trước' };
  if (!c || c.kind === 'probation') return { days: 0, working: false, label: 'Đang thử việc: không cần báo trước' };
  if (c.kind === 'service') return { days: 0, working: false, label: 'HĐ dịch vụ: theo thỏa thuận trong HĐ' };
  if (c.kind === 'indefinite') return { days: 45, working: false, label: 'HĐ không xác định thời hạn: báo trước ít nhất 45 ngày' };
  const months = c.end ? dayjs(c.end).diff(dayjs(c.start), 'month', true) : 12;
  return months >= 12
    ? { days: 30, working: false, label: 'HĐ xác định thời hạn 12–36 tháng: báo trước ít nhất 30 ngày' }
    : { days: 3, working: true, label: 'HĐ dưới 12 tháng: báo trước ít nhất 3 ngày làm việc' };
}

export function noticeShortfall(s: HrState, o: Pick<Offboarding, 'empId' | 'type' | 'noticeDate' | 'lastDay'>) {
  const req = requiredNotice(s, o.empId, o.type, o.noticeDate);
  if (!req.days) return null;
  const minLast = req.working ? addWorkingDays(addDays(o.noticeDate, 1), req.days) : addDays(o.noticeDate, req.days);
  return o.lastDay < minLast ? { ...req, given: daysBetween(o.noticeDate, o.lastDay), minLast } : null;
}

export const offTasksFor = (s: HrState, o: Offboarding): OffTask[] => {
  const e = empById(s, o.empId);
  return (Object.keys(OFF_TASKS) as OffTask[]).filter(t => t !== 'insurance' || !!e?.ins.increasedAt);
};
export const settleDeadline = (o: Offboarding) => addDays(o.lastDay, SETTLE_DAYS);

/* ============================== Định biên & tuyển dụng ============================== */

export const reqHired = (s: HrState, r: Requisition) => s.candidates.filter(c => c.requisitionId === r.id && c.outcome === 'hired').length;
export const reqOpenSlots = (s: HrState, r: Requisition) => (r.status === 'approved' || r.status === 'pending' ? Math.max(0, r.qty - reqHired(s, r)) : 0);
export const reqCandidates = (s: HrState, reqId: string) => s.candidates.filter(c => c.requisitionId === reqId);

/** Quân số còn lại sau khi trừ người đã có lịch nghỉ */
const staying = (s: HrState, e: Employee, today: string) => isStaffAt(s, e, today) && !e.leftDate;

/** So định biên – thực tế – đang tuyển cho một ô chi nhánh × bộ phận */
export function planStatus(s: HrState, branchId: string, deptId: string, today: string, exceptReqId?: string) {
  const plan = s.plan[planKey(branchId, deptId)] || 0;
  const emps = s.employees.filter(e => e.branchId === branchId && deptOf(s, e) === deptId);
  const actual = emps.filter(e => isStaffAt(s, e, today)).length;
  const leaving = emps.filter(e => isStaffAt(s, e, today) && e.leftDate).length;
  const recruiting = s.requisitions
    .filter(r => r.id !== exceptReqId && r.branchId === branchId && positionById(s, r.positionId)?.deptId === deptId)
    .reduce((a, r) => a + reqOpenSlots(s, r), 0);
  const upcoming = emps.filter(e => e.hireDate > today).length;
  const staff = emps.filter(e => staying(s, e, today)).length;
  return { plan, actual, leaving, recruiting, upcoming, gap: plan - staff - upcoming, uncovered: Math.max(0, plan - staff - upcoming - recruiting) };
}

export function planGrid(s: HrState, today: string) {
  return s.branches.map(b => ({
    branch: b,
    cells: s.depts.map(d => ({ dept: d, ...planStatus(s, b.id, d.id, today) })),
  }));
}

export type ReqDraft = Omit<Requisition, 'id' | 'code' | 'status' | 'createdBy' | 'createdAt' | 'log' | 'overPlan'> & { id?: string };

export function validateRequisition(s: HrState, d: ReqDraft, today: string): { errors: string[]; warnings: string[]; overPlan: boolean } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const pos = positionById(s, d.positionId);
  if (!d.branchId) errors.push('Chọn chi nhánh');
  if (!pos) errors.push('Chọn vị trí');
  if (!(d.qty > 0)) errors.push('Số lượng phải lớn hơn 0');
  if (!(d.salaryMin > 0) || !(d.salaryMax >= d.salaryMin)) errors.push('Nhập khung lương hợp lệ (tối thiểu ≤ tối đa)');
  if (!d.deadline || d.deadline < today) errors.push('Hạn tuyển phải từ hôm nay trở đi');
  if (d.reason === 'replace' && !d.replaceEmpIds?.length) errors.push('Chọn nhân sự cần thay thế');
  if (d.reason === 'replace' && (d.replaceEmpIds?.length || 0) > d.qty) errors.push('Số người thay thế nhiều hơn số lượng tuyển');
  let overPlan = false;
  if (pos && d.branchId) {
    const st = planStatus(s, d.branchId, pos.deptId, today, d.id);
    const room = st.plan - (st.actual - st.leaving) - st.upcoming - st.recruiting;
    if (d.qty > room) {
      overPlan = true;
      warnings.push(`Vượt định biên: ${deptById(s, pos.deptId)?.name} ${branchById(s, d.branchId)?.short} định biên ${st.plan}, hiện ${st.actual - st.leaving} người${st.recruiting ? `, đang tuyển ${st.recruiting}` : ''} — còn trống ${Math.max(0, room)}. Cần Ban giám đốc duyệt.`);
    }
    if (d.salaryMin < pos.salaryMin || d.salaryMax > pos.salaryMax) warnings.push(`Khung lương khác khung chuẩn của vị trí (${fmtM(pos.salaryMin)} – ${fmtM(pos.salaryMax)})`);
  }
  return { errors, warnings, overPlan };
}

const norm = (v?: string) => (v || '').replace(/\D/g, '');

/** Kiểm trùng ứng viên: đang trong quy trình, đang là nhân viên, thuộc danh sách không tái tuyển… */
export function candidateDupCheck(s: HrState, d: { phone: string; cccd?: string; id?: string }, today: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const phone = norm(d.phone);
  const cccd = norm(d.cccd);
  const same = (p?: string, c?: string) => (!!phone && norm(p) === phone) || (!!cccd && norm(c) === cccd);
  s.candidates.filter(c => c.id !== d.id && same(c.phone, c.cccd)).forEach(c => {
    const r = reqById(s, c.requisitionId);
    const pos = positionById(s, r?.positionId)?.name;
    if (c.outcome === 'open') errors.push(`Ứng viên ${c.name} đang trong quy trình tuyển ${pos} (${STAGE[c.stage].label})`);
    else if (c.outcome === 'rejected' && c.closedAt && daysBetween(toDay(c.closedAt), today) < 180) warnings.push(`Đã bị loại ở vị trí ${pos} ngày ${fmtD(toDay(c.closedAt))}${c.closeReason ? ` (${c.closeReason})` : ''}`);
  });
  s.employees.filter(e => same(e.phone, e.cccd)).forEach(e => {
    if (!e.leftDate || e.leftDate >= today) errors.push(`Trùng nhân sự đang làm việc: ${e.name} (${e.code})`);
    else if (e.noRehire) errors.push(`${e.name} thuộc danh sách không tái tuyển (nghỉ ${fmtD(e.leftDate)}${e.leaveReason ? ` – ${e.leaveReason}` : ''})`);
    else warnings.push(`Từng làm việc tại công ty: ${e.name}, nghỉ ${fmtD(e.leftDate)}${e.leaveReason ? ` (${e.leaveReason})` : ''}`);
  });
  return { errors, warnings };
}

export const offerNeedsApproval = (r: Requisition | undefined, salary: number) => !!r && (salary < r.salaryMin || salary > r.salaryMax);

/** Ngày ứng viên vào từng bước (lần đầu) */
export const stageAt = (c: Candidate, st: Stage) => c.history.find(h => h.stage === st)?.at;

/* ============================== Mã chứng từ ============================== */

export function nextCode(codes: string[], prefix: string, date: string, pad = 3) {
  const head = `${prefix}${dayjs(date).format('YYMM')}-`;
  const max = codes.filter(c => c.startsWith(head)).reduce((m, c) => Math.max(m, Number(c.slice(head.length)) || 0), 0);
  return `${head}${String(max + 1).padStart(pad, '0')}`;
}
export const CONTRACT_PREFIX: Record<ContractKind, string> = { probation: 'TV', fixed: 'HĐLĐ', indefinite: 'HĐLĐ', service: 'HĐDV' };
export function nextEmpCode(s: HrState) {
  const max = s.employees.reduce((m, e) => Math.max(m, Number(e.code.replace(/\D/g, '')) || 0), 0);
  return `NV${String(max + 1).padStart(4, '0')}`;
}

/** Bỏ dấu tiếng Việt để tìm kiếm */
const DIACRITICS = new RegExp('[\u0300-\u036f]', 'g');
export const normText = (x: string) => x.normalize('NFD').replace(DIACRITICS, '').replace(/đ/gi, 'd').toLowerCase();

/* ============================== Lương thưởng, phúc lợi, nghỉ phép ============================== */

/** Đến hạn xét tăng lương sau số tháng không thay đổi lương */
export const SALARY_REVIEW_MONTHS = 12;
/** Phép năm cơ bản; cứ đủ 5 năm làm việc được thêm 1 ngày */
export const ANNUAL_LEAVE_BASE = 12;

/** Đối tượng hưởng phụ cấp */
export type AllowScope = 'all' | 'lead' | 'manager' | 'director' | 'gphn' | 'medicalLead';
export const ALLOW_SCOPE: Record<AllowScope, string> = {
  all: 'Mọi người có HĐLĐ / thử việc', lead: 'Trưởng nhóm', manager: 'Quản lý', director: 'Ban giám đốc',
  gphn: 'Vị trí cần GPHN (sau thử việc)', medicalLead: 'Người phụ trách chuyên môn',
};
export interface AllowanceRule {
  id: string; name: string; amount: number; scope: AllowScope;
  /** Tính theo ngày công thực tế (ăn trưa) */
  prorate: boolean;
  /** Phần miễn thuế TNCN mỗi tháng: 0 = chịu thuế, -1 = miễn toàn bộ */
  taxFree: number;
  /** Tính vào tiền lương đóng BHXH */
  insurable: boolean;
  active: boolean;
}
/** Bảng phụ cấp mặc định — sửa tại Bảng lương › Thiết lập */
export const DEFAULT_ALLOWANCES: AllowanceRule[] = [
  { id: 'lunch', name: 'Ăn trưa', amount: 730000, scope: 'all', prorate: true, taxFree: 730000, insurable: false, active: true },
  { id: 'resp-lead', name: 'Trách nhiệm', amount: 1000000, scope: 'lead', prorate: false, taxFree: 0, insurable: true, active: true },
  { id: 'resp-mgr', name: 'Trách nhiệm', amount: 2500000, scope: 'manager', prorate: false, taxFree: 0, insurable: true, active: true },
  { id: 'phone-mgr', name: 'Điện thoại', amount: 500000, scope: 'manager', prorate: false, taxFree: -1, insurable: false, active: true },
  { id: 'resp-dir', name: 'Trách nhiệm', amount: 5000000, scope: 'director', prorate: false, taxFree: 0, insurable: true, active: true },
  { id: 'phone-dir', name: 'Điện thoại', amount: 1000000, scope: 'director', prorate: false, taxFree: -1, insurable: false, active: true },
  { id: 'med', name: 'Chuyên môn y tế', amount: 1000000, scope: 'gphn', prorate: false, taxFree: 0, insurable: true, active: true },
  { id: 'med-lead', name: 'Phụ trách chuyên môn', amount: 3000000, scope: 'medicalLead', prorate: false, taxFree: 0, insurable: true, active: true },
];

export interface Allowance { name: string; amount: number; rule?: AllowanceRule }
/** Phụ cấp cố định theo cấp bậc / chuyên môn */
export function allowancesOf(s: HrState, e: Employee, d: string, rules: AllowanceRule[] = DEFAULT_ALLOWANCES): Allowance[] {
  const p = positionById(s, e.positionId);
  const c = currentContract(s, e.id, d);
  if (!p || !c || c.kind === 'service') return [];
  const hit = (r: AllowanceRule) => r.scope === 'all' || r.scope === p.level
    || (r.scope === 'gphn' && p.license === 'gphn' && c.kind !== 'probation') || (r.scope === 'medicalLead' && !!e.medicalLead);
  return rules.filter(r => r.active && hit(r)).map(r => ({ name: r.name, amount: r.amount, rule: r }));
}

export function payPolicyOf(s: HrState, e: Employee) {
  const dept = deptOf(s, e);
  if (dept === 'consult') return 'Lương cơ bản + hoa hồng doanh số';
  if (dept === 'tech' || dept === 'doctor') return 'Lương cơ bản + % thủ thuật';
  return 'Lương cố định';
}

/** Lần thay đổi lương gần nhất (ký HĐ có lương khác hoặc quyết định điều chỉnh) */
export function lastPayChange(s: HrState, e: Employee, d: string): { date: string; salary: number; how: string } | null {
  const events = [
    ...empContracts(s, e.id).filter(c => c.start <= d).map(c => ({ date: c.start, salary: c.kind === 'probation' ? c.salary : c.salary, how: `HĐ ${c.code}` })),
    ...(e.moves || []).filter(m => m.applied && m.salary && m.date <= d).map(m => ({ date: m.date, salary: m.salary!, how: `Quyết định ${m.decisionNo}` })),
  ].sort((a, b) => a.date.localeCompare(b.date));
  let last: (typeof events)[number] | null = events[0] || null;
  events.forEach((x, i) => { if (i > 0 && x.salary !== events[i - 1].salary) last = x; });
  return last;
}

/** Thưởng nhận trong 12 tháng gần nhất */
export const rewards12m = (e: Employee, d: string) =>
  (e.records || []).filter(r => r.kind === 'reward' && r.date > addDays(d, -365) && r.date <= d).reduce((a, r) => a + (r.amount || 0), 0);

/** Phép năm của năm hiện tại: 12 ngày + thâm niên, tính theo tỷ lệ tháng làm việc nếu vào làm trong năm */
export function leaveBalance(s: HrState, e: Employee, d: string) {
  const c = currentContract(s, e.id, d);
  const yearStart = `${d.slice(0, 4)}-01-01`;
  const seniority = Math.floor(dayjs(d).diff(dayjs(e.hireDate), 'year', true) / 5);
  const entitledFull = ANNUAL_LEAVE_BASE + seniority;
  const from = e.hireDate > yearStart ? e.hireDate : yearStart;
  const months = Math.max(0, 12 - dayjs(from).month() - (dayjs(from).date() > 15 ? 1 : 0));
  const eligible = !!c && c.kind !== 'service';
  const entitled = eligible ? Math.round((entitledFull * months) / 12) : 0;
  const year = d.slice(0, 4);
  const used = (e.leaveUsed || 0) + (e.leaveRecords || []).filter(r => r.kind === 'annual' && r.from.slice(0, 4) === year).reduce((a, r) => a + r.days, 0);
  const carried = e.leaveCarried || 0;
  return { entitled, seniority, used, carried, remain: entitled + carried - used, probation: c?.kind === 'probation' };
}

/* ----- Nghỉ phép ----- */
export type LeaveKind = 'annual' | 'personal' | 'sick' | 'unpaid' | 'maternity';
export const LEAVE_KIND: Record<LeaveKind, { label: string; badge: string }> = {
  annual: { label: 'Phép năm', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  personal: { label: 'Việc riêng có lương', badge: 'bg-teal-50 text-teal-700 border-teal-200' },
  sick: { label: 'Nghỉ ốm (BHXH chi trả)', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  unpaid: { label: 'Nghỉ không lương', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  maternity: { label: 'Thai sản', badge: 'bg-pink-50 text-pink-700 border-pink-200' },
};
export interface LeaveRecord { id: string; kind: LeaveKind; from: string; to: string; days: number; reason?: string; by: string; at: number }

/** Số ngày làm việc (bỏ Chủ nhật) trong khoảng */
export function workingDaysBetween(from: string, to: string) {
  let n = 0;
  for (let d = dayjs(from); !d.isAfter(dayjs(to), 'day'); d = d.add(1, 'day')) if (d.day() !== 0) n += 1;
  return n;
}
export const onLeaveAt = (e: Employee, d: string) => (e.leaveRecords || []).find(r => r.from <= d && d <= r.to);

export function validateLeave(s: HrState, e: Employee, r: Omit<LeaveRecord, 'id' | 'by' | 'at'>, today: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!r.from || !r.to || r.to < r.from) errors.push('Chọn ngày bắt đầu và kết thúc hợp lệ');
  if (!(r.days > 0)) errors.push('Số ngày nghỉ phải lớn hơn 0');
  if (r.from && r.from < e.hireDate) errors.push('Ngày nghỉ trước ngày vào làm');
  if (e.leftDate && r.to > e.leftDate) errors.push(`Nhân sự nghỉ việc từ ${fmtD(e.leftDate)}`);
  const overlap = (e.leaveRecords || []).find(x => x.from <= r.to && r.from <= x.to);
  if (overlap) errors.push(`Trùng với đợt nghỉ ${fmtD(overlap.from)} – ${fmtD(overlap.to)} (${LEAVE_KIND[overlap.kind].label})`);
  const c = currentContract(s, e.id, r.from || today);
  if (r.kind === 'annual') {
    const b = leaveBalance(s, e, r.from || today);
    if (c?.kind === 'service') errors.push('Cộng tác viên không có phép năm');
    else if (r.days > b.remain) errors.push(`Vượt số phép năm còn lại (${b.remain} ngày) — phần vượt ghi nhận nghỉ không lương`);
    if (c?.kind === 'probation') warnings.push('Đang thử việc: phép năm được tính nhưng thường chỉ dùng sau khi ký HĐ chính thức');
  }
  if (r.kind === 'personal' && r.days > 3) warnings.push('Việc riêng có lương theo luật: kết hôn 3 ngày, con kết hôn 1 ngày, bố mẹ / vợ chồng / con mất 3 ngày');
  if (r.kind === 'maternity' && e.gender === 'Nữ' && r.days < 150 && r.days > 30) warnings.push('Thai sản thường 6 tháng — kiểm tra lại thời gian và tạm hoãn HĐ');
  if (r.kind === 'sick') warnings.push('Cần giấy chứng nhận nghỉ việc hưởng BHXH để làm hồ sơ chi trả');
  if (r.from && r.from < addDays(today, -30)) warnings.push('Ghi nhận cho ngày quá 30 ngày trước — kiểm tra lại bảng công đã chốt');
  return { errors, warnings };
}
